import { useRef, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { FileSpreadsheet } from "lucide-react";

export default function ImportExcel({ branchId, branchName, onDone }) {
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [report, setReport] = useState(null);
  const inputRef = useRef(null);

  const doImport = async () => {
    if (!file || !branchId || busy) return;
    setBusy(true);
    setReport(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post(`/products/import-excel?branch_id=${branchId}`, fd);
      setReport(r.data);
      toast.success(`Import selesai: ${r.data.created} produk baru, ${r.data.updated} stok ditambah`);
      onDone?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal mengimport file");
    } finally {
      setBusy(false);
      setFile(null);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  return (
    <div data-testid="import-excel" className="bg-white rounded-2xl border border-[#E5DECF] p-4 space-y-3">
      <div>
        <h3 className="font-display font-black text-base">📥 Import Excel — Input Massal Belanja Baru</h3>
        <p className="text-xs text-stone-500 mt-1">
          Upload file .xlsx daftar belanja (sheet INPUT PRODUK atau KONVERSI STOK). Kolom yang dibaca: Nama Produk, Kategori, Satuan, Stok, Harga Jual Final, HPP Estimasi, Barcode, URL Gambar, Status Penerimaan.
        </p>
        <p className="text-xs text-stone-500 mt-1">
          Baris berstatus selain "Diterima/Diterima sebagian" atau stok 0 otomatis dilewati. Harga jual kosong/0 → otomatis HPP + 15% dibulatkan ke atas Rp100. Produk yang sudah ada (nama/barcode sama) tidak dibuat ulang — stoknya ditambah.
        </p>
        <p className="text-xs mt-2 font-bold text-emerald-800" data-testid="import-excel-branch">Stok masuk ke cabang: {branchName || "—"}</p>
      </div>
      <div className="flex items-center gap-2 flex-wrap">
        <input ref={inputRef} id="import-excel-input" data-testid="import-excel-input" type="file" accept=".xlsx" className="hidden" onChange={(e) => setFile(e.target.files?.[0] || null)} />
        <label htmlFor="import-excel-input" data-testid="import-excel-pick-btn" className="cursor-pointer inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-stone-900 text-white text-xs font-bold hover:bg-emerald-700 transition">
          <FileSpreadsheet className="w-3.5 h-3.5" /> {file ? file.name : "Pilih File .xlsx"}
        </label>
        <button data-testid="import-excel-submit-btn" onClick={doImport} disabled={!file || !branchId || busy} className="px-4 py-2 rounded-full bg-emerald-700 hover:bg-emerald-800 text-white text-xs font-bold disabled:opacity-50">
          {busy ? "Mengimport…" : "Import Sekarang"}
        </button>
      </div>
      {report && (
        <div data-testid="import-excel-result" className="rounded-xl border border-emerald-200 bg-emerald-50/60 p-3 text-xs space-y-2">
          <p className="font-bold text-emerald-800">✓ {report.created} produk baru ditambahkan · ↻ {report.updated} produk lama ditambah stoknya <span className="font-normal text-stone-400">(sheet: {report.sheet})</span></p>
          {report.no_price?.length > 0 && (
            <div>
              <p className="font-bold text-amber-700">⚠ {report.no_price.length} produk tanpa harga (HPP kosong) — edit manual di tab Produk:</p>
              <ul className="mt-0.5 space-y-0.5 text-stone-600 max-h-24 overflow-y-auto">{report.no_price.map((n, i) => <li key={i}>• {n}</li>)}</ul>
            </div>
          )}
          {report.skipped?.length > 0 && (
            <div>
              <p className="font-bold text-stone-500">✗ {report.skipped.length} baris dilewati:</p>
              <ul className="mt-0.5 space-y-0.5 text-stone-500 max-h-24 overflow-y-auto">{report.skipped.map((s, i) => <li key={i}>• {s.name} <span className="text-stone-400">({s.reason})</span></li>)}</ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
